import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CascadeNode, CascadeEdge, DataField, FuzzinessLevel, CaptureMethod, ActiveChoice, ChoiceOption, ValidationRuleset, CaptureOwner, DobFormat, AddressMethod, NationalIdType, TaxIdType } from './types'
import { getDefaultCollectedFields, getDefaultVerifiedFields, CAPTURE_METHOD_TYPE_CONFIG } from './types'
import { DEFAULT_DATA_FIELDS, getChoicesForNode } from './data'
import { cascadeApi } from '@api/client'

// ─── Store Interface ───────────────────────────────────────────────────

/** Snapshot of a single country's cascade */
interface CountryCascade {
  country: string
  countryCode: string
  nodes: CascadeNode[]
  edges: CascadeEdge[]
  notes: string
}

/** A saved deal project with multiple country cascades */
interface SavedProject {
  id: string
  dealId: number | null
  dealName: string
  cascades: CountryCascade[]
  updatedAt: string
}

interface CascadeBuilderState {
  // Deal association
  dealId: number | null
  dealName: string

  // Active country (the one currently on the canvas)
  country: string
  countryCode: string
  ruleset: string
  ruleDescription: string

  // Data fields (what comes in from customer)
  dataFields: DataField[]

  // Canvas nodes & edges (for the active country)
  nodes: CascadeNode[]
  edges: CascadeEdge[]

  // Other country cascades for this deal (not currently active)
  countryCascades: CountryCascade[]

  // UI state
  selectedNodeId: string | null
  draggingNodeId: string | null
  connectingFrom: string | null
  showSourcePicker: boolean
  showDataFieldPanel: boolean
  zoom: number
  panX: number
  panY: number
  notes: string

  // Choice system
  activeChoice: ActiveChoice | null
  fadingChoiceIds: string[] // IDs of choices currently fading out

  // Saved projects
  savedProjects: SavedProject[]

  // Actions
  setDeal: (dealId: number | null, dealName: string) => void
  setCountry: (country: string, countryCode: string) => void
  addCountry: (country: string, countryCode: string) => void
  switchCountry: (countryCode: string) => void
  removeCountry: (countryCode: string) => void
  setRuleset: (ruleset: string, description: string) => void
  setNotes: (notes: string) => void

  // Data field actions
  toggleDataField: (fieldId: string) => void
  setFieldFuzziness: (fieldId: string, fuzziness: FuzzinessLevel) => void
  setFieldCaptureMethod: (fieldId: string, method: CaptureMethod) => void

  // Node actions
  addNode: (node: CascadeNode) => void
  updateNode: (id: string, updates: Partial<CascadeNode>) => void
  removeNode: (id: string) => void
  moveNode: (id: string, x: number, y: number) => void
  selectNode: (id: string | null) => void
  setDragging: (id: string | null) => void

  // Edge actions
  addEdge: (edge: CascadeEdge) => void
  updateEdge: (id: string, updates: Partial<CascadeEdge>) => void
  removeEdge: (id: string) => void
  startConnecting: (fromId: string) => void
  finishConnecting: (toId: string) => void
  cancelConnecting: () => void

  // Choice actions
  showChoices: (sourceNodeId: string) => void
  pickChoice: (choiceOption: ChoiceOption) => void
  dismissChoices: () => void

  // Node detail editing (for capture-method nodes)
  setCaptureOwner: (nodeId: string, owner: CaptureOwner) => void
  setCaptureVendorName: (nodeId: string, name: string) => void
  toggleCollectedField: (nodeId: string, fieldId: string) => void
  setCollectedFieldOptional: (nodeId: string, fieldId: string, optional: boolean) => void
  setCollectedFieldDobFormat: (nodeId: string, fieldId: string, format: DobFormat) => void
  setCollectedFieldAddressMethod: (nodeId: string, fieldId: string, method: AddressMethod) => void
  setCollectedFieldPossessionVerified: (nodeId: string, fieldId: string, verified: boolean) => void
  setCollectedFieldNationalIdType: (nodeId: string, fieldId: string, idType: NationalIdType) => void
  setCollectedFieldNationalIdOther: (nodeId: string, fieldId: string, other: string) => void
  setCollectedFieldTaxIdType: (nodeId: string, fieldId: string, idType: TaxIdType) => void
  setCollectedFieldTaxIdOther: (nodeId: string, fieldId: string, other: string) => void
  addCustomCollectedField: (nodeId: string, label: string) => void
  removeCustomCollectedField: (nodeId: string, fieldId: string) => void
  setCollectedFieldFuzziness: (nodeId: string, fieldId: string, fuzziness: FuzzinessLevel) => void
  setNodeNotes: (nodeId: string, notes: string) => void

  // Validation node editing
  setValidationVendor: (nodeId: string, vendor: string) => void
  setValidationRuleset: (nodeId: string, ruleset: ValidationRuleset) => void
  toggleVerifiedField: (nodeId: string, fieldId: string) => void
  setVerifiedFieldFuzziness: (nodeId: string, fieldId: string, fuzziness: FuzzinessLevel) => void
  addSecondaryVendor: (sourceNodeId: string) => void

  // UI actions
  setShowSourcePicker: (show: boolean) => void
  setShowDataFieldPanel: (show: boolean) => void
  setZoom: (zoom: number) => void
  setPan: (x: number, y: number) => void

  // Project management
  saveProject: () => void
  loadProject: (id: string) => void
  deleteProject: (id: string) => void
  resetCascade: () => void
  loadProjectsFromDb: (dealId: number) => Promise<void>
}

// ─── Initial customer node ─────────────────────────────────────────────

const INITIAL_CUSTOMER_NODE: CascadeNode = {
  id: 'customer',
  type: 'customer-input',
  label: 'Customer',
  sublabel: 'provides their details',
  x: 80,
  y: 200,
}

// ─── Node width constant for positioning ───────────────────────────────
const NODE_GAP_X = 300

// ─── Store ─────────────────────────────────────────────────────────────

export const useCascadeStore = create<CascadeBuilderState>()(persist((set, get) => ({
  dealId: null,
  dealName: '',
  country: '',
  countryCode: '',
  ruleset: '',
  ruleDescription: '',
  dataFields: DEFAULT_DATA_FIELDS.map(f => ({ ...f })),
  nodes: [{ ...INITIAL_CUSTOMER_NODE }],
  edges: [],
  countryCascades: [],
  selectedNodeId: null,
  draggingNodeId: null,
  connectingFrom: null,
  showSourcePicker: false,
  showDataFieldPanel: false,
  zoom: 1,
  panX: 0,
  panY: 0,
  notes: '',
  activeChoice: null,
  fadingChoiceIds: [],
  savedProjects: [],

  setDeal: (dealId, dealName) => set({ dealId, dealName }),

  setCountry: (country, countryCode) => set({ country, countryCode }),

  addCountry: (country, countryCode) => {
    const s = get()
    // Don't add if already exists
    if (s.countryCode === countryCode || s.countryCascades.some(c => c.countryCode === countryCode)) return

    // Save current country to countryCascades (if one is active)
    const updated = [...s.countryCascades]
    if (s.countryCode) {
      updated.push({
        country: s.country,
        countryCode: s.countryCode,
        nodes: s.nodes.map(n => ({ ...n })),
        edges: s.edges.map(e => ({ ...e })),
        notes: s.notes,
      })
    }

    // Switch to the new country with a fresh canvas
    set({
      countryCascades: updated,
      country,
      countryCode,
      nodes: [{ ...INITIAL_CUSTOMER_NODE }],
      edges: [],
      notes: '',
      selectedNodeId: null,
      connectingFrom: null,
      activeChoice: null,
      fadingChoiceIds: [],
    })
  },

  switchCountry: (targetCode) => {
    const s = get()
    if (s.countryCode === targetCode) return

    const target = s.countryCascades.find(c => c.countryCode === targetCode)
    if (!target) return

    // Save current country, remove target from list, load target
    const updated = s.countryCascades
      .filter(c => c.countryCode !== targetCode)
      .concat(s.countryCode ? [{
        country: s.country,
        countryCode: s.countryCode,
        nodes: s.nodes.map(n => ({ ...n })),
        edges: s.edges.map(e => ({ ...e })),
        notes: s.notes,
      }] : [])

    set({
      countryCascades: updated,
      country: target.country,
      countryCode: target.countryCode,
      nodes: target.nodes.map(n => ({ ...n })),
      edges: target.edges.map(e => ({ ...e })),
      notes: target.notes,
      selectedNodeId: null,
      connectingFrom: null,
      activeChoice: null,
      fadingChoiceIds: [],
    })
  },

  removeCountry: (code) => {
    const s = get()
    if (s.countryCode === code) {
      // Removing the active country — switch to first other or reset
      const remaining = s.countryCascades
      if (remaining.length > 0) {
        const next = remaining[0]
        set({
          countryCascades: remaining.slice(1),
          country: next.country,
          countryCode: next.countryCode,
          nodes: next.nodes.map(n => ({ ...n })),
          edges: next.edges.map(e => ({ ...e })),
          notes: next.notes,
          selectedNodeId: null,
        })
      } else {
        set({
          country: '',
          countryCode: '',
          nodes: [{ ...INITIAL_CUSTOMER_NODE }],
          edges: [],
          notes: '',
          selectedNodeId: null,
        })
      }
    } else {
      set({ countryCascades: s.countryCascades.filter(c => c.countryCode !== code) })
    }
  },

  setRuleset: (ruleset, description) => set({ ruleset, ruleDescription: description }),
  setNotes: (notes) => set({ notes }),

  // Data fields
  toggleDataField: (fieldId) => set((s) => ({
    dataFields: s.dataFields.map(f =>
      f.id === fieldId ? { ...f, enabled: !f.enabled } : f
    ),
  })),

  setFieldFuzziness: (fieldId, fuzziness) => set((s) => ({
    dataFields: s.dataFields.map(f =>
      f.id === fieldId ? { ...f, fuzziness } : f
    ),
  })),

  setFieldCaptureMethod: (fieldId, method) => set((s) => ({
    dataFields: s.dataFields.map(f =>
      f.id === fieldId ? { ...f, captureMethod: method } : f
    ),
  })),

  // Nodes
  addNode: (node) => set((s) => ({ nodes: [...s.nodes, node] })),

  updateNode: (id, updates) => set((s) => ({
    nodes: s.nodes.map(n => n.id === id ? { ...n, ...updates } : n),
  })),

  removeNode: (id) => set((s) => ({
    nodes: s.nodes.filter(n => n.id !== id),
    edges: s.edges.filter(e => e.from !== id && e.to !== id),
    selectedNodeId: s.selectedNodeId === id ? null : s.selectedNodeId,
  })),

  moveNode: (id, x, y) => set((s) => ({
    nodes: s.nodes.map(n => n.id === id ? { ...n, x, y } : n),
  })),

  selectNode: (id) => set({ selectedNodeId: id }),
  setDragging: (id) => set({ draggingNodeId: id }),

  // Edges
  addEdge: (edge) => set((s) => {
    const exists = s.edges.some(e => e.from === edge.from && e.to === edge.to)
    if (exists) return s
    return { edges: [...s.edges, edge] }
  }),

  updateEdge: (id, updates) => set((s) => ({
    edges: s.edges.map(e => e.id === id ? { ...e, ...updates } : e),
  })),

  removeEdge: (id) => set((s) => ({
    edges: s.edges.filter(e => e.id !== id),
  })),

  startConnecting: (fromId) => set({ connectingFrom: fromId }),

  finishConnecting: (toId) => {
    const { connectingFrom, edges } = get()
    if (!connectingFrom || connectingFrom === toId) {
      set({ connectingFrom: null })
      return
    }
    const exists = edges.some(e => e.from === connectingFrom && e.to === toId)
    if (!exists) {
      const newEdge: CascadeEdge = {
        id: `edge_${Date.now()}`,
        from: connectingFrom,
        to: toId,
      }
      set((s) => ({ edges: [...s.edges, newEdge], connectingFrom: null }))
    } else {
      set({ connectingFrom: null })
    }
  },

  cancelConnecting: () => set({ connectingFrom: null }),

  // ─── Choice system ─────────────────────────────────────────────
  showChoices: (sourceNodeId) => {
    const { nodes, activeChoice } = get()
    // Toggle off if already showing choices for this node
    if (activeChoice?.sourceNodeId === sourceNodeId) {
      set({ activeChoice: null, fadingChoiceIds: [] })
      return
    }

    const sourceNode = nodes.find(n => n.id === sourceNodeId)
    if (!sourceNode) return

    const options = getChoicesForNode(sourceNode.type)
    if (options.length === 0) return

    set({
      activeChoice: {
        sourceNodeId,
        options,
        baseX: sourceNode.x + NODE_GAP_X,
        baseY: sourceNode.y + 28, // offset for hero icon above card (mt-7 = 28px)
      },
      fadingChoiceIds: [],
    })
  },

  pickChoice: (choiceOption) => {
    const { activeChoice, nodes } = get()
    if (!activeChoice) return

    const sourceNode = nodes.find(n => n.id === activeChoice.sourceNodeId)
    if (!sourceNode) return

    // If this choice has children, drill down into sub-choices
    if (choiceOption.children && choiceOption.children.length > 0) {
      const otherIds = activeChoice.options
        .filter(o => o.id !== choiceOption.id)
        .map(o => o.id)
      set({ fadingChoiceIds: otherIds })
      setTimeout(() => {
        set({
          activeChoice: {
            sourceNodeId: activeChoice.sourceNodeId,
            options: choiceOption.children!,
            baseX: activeChoice.baseX,
            baseY: activeChoice.baseY,
          },
          fadingChoiceIds: [],
        })
      }, 300)
      return
    }

    // Mark non-picked choices as fading
    const otherIds = activeChoice.options
      .filter(o => o.id !== choiceOption.id)
      .map(o => o.id)

    set({ fadingChoiceIds: otherIds })

    // After fade animation, create the real node + edge
    setTimeout(() => {
      // Position the new node — offset down if the source already has outgoing edges
      const { edges: currentEdges, nodes: currentNodes } = get()
      const existingTargets = currentEdges
        .filter(e => e.from === activeChoice.sourceNodeId)
        .map(e => currentNodes.find(n => n.id === e.to))
        .filter(Boolean) as CascadeNode[]
      const yOffset = existingTargets.length > 0
        ? (Math.max(...existingTargets.map(t => t.y)) - activeChoice.baseY + 100)
        : 0

      // Determine label for decision nodes
      let label = choiceOption.label
      let sublabel = choiceOption.sublabel
      if (choiceOption.nodeType === 'decision') {
        sublabel = choiceOption.label.toLowerCase().replace(' ', '-')
      }

      // For capture-method nodes, override the icon in the label
      const isCaptureMethod = choiceOption.nodeType === 'capture-method'
      const isValidation = choiceOption.nodeType === 'validation'
      const isFraudCheck = choiceOption.nodeType === 'fraud-check'
      const isRiskCheck = choiceOption.nodeType === 'risk-check'
      const isCheckNode = isValidation || isFraudCheck || isRiskCheck
      const methodConfig = isCaptureMethod && choiceOption.captureMethodType
        ? (CAPTURE_METHOD_TYPE_CONFIG[choiceOption.captureMethodType] || null)
        : null

      const nodeX = activeChoice.baseX
      const nodeY = activeChoice.baseY + yOffset

      const newNode: CascadeNode = {
        id: `node_${Date.now()}`,
        type: choiceOption.nodeType,
        label: isCaptureMethod && methodConfig ? methodConfig.label : isValidation ? 'Identity Check' : isFraudCheck ? 'Fraud Check' : isRiskCheck ? 'Risk Check' : label,
        sublabel: isCheckNode ? undefined : sublabel,
        vendor: choiceOption.vendor,
        vendorId: choiceOption.vendorId,
        description: choiceOption.description,
        verifies: choiceOption.verifies,
        requiresConsent: choiceOption.requiresConsent,
        price: choiceOption.price,
        x: nodeX,
        y: nodeY,
        // Capture-method-specific
        captureMethodType: choiceOption.captureMethodType,
        captureOwner: isCaptureMethod ? 'client' as const : undefined,
        collectedFields: isCaptureMethod ? getDefaultCollectedFields(choiceOption.captureMethodType) : undefined,
        nodeNotes: '',
        // Validation-specific
        validationVendor: isValidation ? '' : undefined,
        validationRuleset: isValidation ? '1+1' : undefined,
        verifiedFields: isValidation ? getDefaultVerifiedFields() : undefined,
      }

      // Determine edge condition for decision nodes
      let edgeCondition: CascadeEdge['condition'] = undefined
      let edgeLabel: string | undefined = undefined

      if (choiceOption.nodeType === 'decision') {
        if (choiceOption.label === 'Pass') {
          edgeCondition = 'pass'
          edgeLabel = 'Pass'
        } else if (choiceOption.label === 'Fail') {
          edgeCondition = 'fail'
          edgeLabel = 'Fail'
        } else if (choiceOption.label === 'Manual Review') {
          edgeCondition = 'review'
          edgeLabel = 'Review'
        }
      }

      const newEdge: CascadeEdge = {
        id: `edge_${Date.now()}`,
        from: activeChoice.sourceNodeId,
        to: newNode.id,
        condition: edgeCondition,
        label: edgeLabel,
      }

      set((s) => ({
        nodes: [...s.nodes, newNode],
        edges: [...s.edges, newEdge],
        activeChoice: null,
        fadingChoiceIds: [],
        // Auto-select the new node so the detail panel opens
        selectedNodeId: (isCaptureMethod || isCheckNode) ? newNode.id : s.selectedNodeId,
      }))
    }, 300)
  },

  dismissChoices: () => set({ activeChoice: null, fadingChoiceIds: [] }),

  // ─── Node detail editing ─────────────────────────────────────────
  setCaptureOwner: (nodeId, owner) => set((s) => ({
    nodes: s.nodes.map(n => n.id === nodeId ? { ...n, captureOwner: owner } : n),
  })),

  setCaptureVendorName: (nodeId, name) => set((s) => ({
    nodes: s.nodes.map(n => n.id === nodeId ? { ...n, captureVendorName: name } : n),
  })),

  toggleCollectedField: (nodeId, fieldId) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, enabled: !f.enabled } : f
        ),
      }
    }),
  })),

  setCollectedFieldOptional: (nodeId, fieldId, optional) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, optional } : f
        ),
      }
    }),
  })),

  setCollectedFieldDobFormat: (nodeId, fieldId, format) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, dobFormat: format } : f
        ),
      }
    }),
  })),

  setCollectedFieldAddressMethod: (nodeId, fieldId, method) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, addressMethod: method } : f
        ),
      }
    }),
  })),

  setCollectedFieldPossessionVerified: (nodeId, fieldId, verified) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, possessionVerified: verified } : f
        ),
      }
    }),
  })),

  setCollectedFieldNationalIdType: (nodeId, fieldId, idType) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, nationalIdType: idType } : f
        ),
      }
    }),
  })),

  setCollectedFieldNationalIdOther: (nodeId, fieldId, other) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, nationalIdOther: other } : f
        ),
      }
    }),
  })),

  setCollectedFieldTaxIdType: (nodeId, fieldId, idType) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, taxIdType: idType } : f
        ),
      }
    }),
  })),

  setCollectedFieldTaxIdOther: (nodeId, fieldId, other) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, taxIdOther: other } : f
        ),
      }
    }),
  })),

  addCustomCollectedField: (nodeId, label) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      const id = `custom_${Date.now()}`
      return {
        ...n,
        collectedFields: [
          ...n.collectedFields,
          { id, label, enabled: true, optional: true, fuzziness: 'strict' as const, isCustom: true },
        ],
      }
    }),
  })),

  removeCustomCollectedField: (nodeId, fieldId) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.filter(f => f.id !== fieldId),
      }
    }),
  })),

  setCollectedFieldFuzziness: (nodeId, fieldId, fuzziness) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.collectedFields) return n
      return {
        ...n,
        collectedFields: n.collectedFields.map(f =>
          f.id === fieldId ? { ...f, fuzziness } : f
        ),
      }
    }),
  })),

  setNodeNotes: (nodeId, notes) => set((s) => ({
    nodes: s.nodes.map(n => n.id === nodeId ? { ...n, nodeNotes: notes } : n),
  })),

  // Validation node editing
  setValidationVendor: (nodeId, vendor) => set((s) => ({
    nodes: s.nodes.map(n => n.id === nodeId ? { ...n, validationVendor: vendor } : n),
  })),

  setValidationRuleset: (nodeId, ruleset) => set((s) => ({
    nodes: s.nodes.map(n => n.id === nodeId ? { ...n, validationRuleset: ruleset } : n),
  })),

  toggleVerifiedField: (nodeId, fieldId) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.verifiedFields) return n
      return {
        ...n,
        verifiedFields: n.verifiedFields.map(f =>
          f.id === fieldId ? { ...f, enabled: !f.enabled } : f
        ),
      }
    }),
  })),

  setVerifiedFieldFuzziness: (nodeId, fieldId, fuzziness) => set((s) => ({
    nodes: s.nodes.map(n => {
      if (n.id !== nodeId || !n.verifiedFields) return n
      return {
        ...n,
        verifiedFields: n.verifiedFields.map(f =>
          f.id === fieldId ? { ...f, fuzziness } : f
        ),
      }
    }),
  })),

  addSecondaryVendor: (sourceNodeId) => {
    const { nodes, edges } = get()
    const sourceNode = nodes.find(n => n.id === sourceNodeId)
    if (!sourceNode || sourceNode.type !== 'validation') return

    // Find the lowest existing child vendor to stack below
    const childVendors = edges
      .filter(e => e.from === sourceNodeId)
      .map(e => nodes.find(n => n.id === e.to))
      .filter((n): n is CascadeNode => !!n && n.type === 'validation' && !!n.isSecondaryVendor)
    const lowestY = childVendors.length > 0
      ? Math.max(...childVendors.map(n => n.y))
      : sourceNode.y

    const newNode: CascadeNode = {
      id: `node_${Date.now()}`,
      type: 'validation',
      label: 'Validation',
      sublabel: 'Click to configure vendor',
      x: sourceNode.x,
      y: lowestY + 120,
      validationVendor: '',
      isSecondaryVendor: true,
      nodeNotes: '',
    }

    const newEdge: CascadeEdge = {
      id: `edge_${Date.now()}`,
      from: sourceNodeId,
      to: newNode.id,
    }

    set((s) => ({
      nodes: [...s.nodes, newNode],
      edges: [...s.edges, newEdge],
      selectedNodeId: newNode.id,
    }))
  },

  // UI
  setShowSourcePicker: (show) => set({ showSourcePicker: show }),
  setShowDataFieldPanel: (show) => set({ showDataFieldPanel: show }),
  setZoom: (zoom) => set({ zoom: Math.max(0.3, Math.min(2, zoom)) }),
  setPan: (x, y) => set({ panX: x, panY: y }),

  // Project management
  saveProject: () => {
    const s = get()
    if (!s.dealName && !s.country) return

    // Collect all cascades: active + stored
    const allCascades: CountryCascade[] = [
      ...s.countryCascades.map(c => ({ ...c, nodes: c.nodes.map(n => ({ ...n })), edges: c.edges.map(e => ({ ...e })) })),
    ]
    if (s.countryCode) {
      allCascades.push({
        country: s.country,
        countryCode: s.countryCode,
        nodes: s.nodes.map(n => ({ ...n })),
        edges: s.edges.map(e => ({ ...e })),
        notes: s.notes,
      })
    }

    const id = `project_${Date.now()}`
    const project: SavedProject = {
      id,
      dealId: s.dealId,
      dealName: s.dealName,
      cascades: allCascades,
      updatedAt: new Date().toISOString(),
    }

    set((s) => ({
      savedProjects: [
        ...s.savedProjects.filter(p => p.dealName !== project.dealName || p.dealId !== project.dealId),
        project,
      ],
    }))

    // Persist to DB if associated with a deal
    if (project.dealId) {
      cascadeApi.saveProject({
        id: project.id,
        dealId: project.dealId,
        dealName: project.dealName,
        cascades: project.cascades,
      }).catch((err) => console.error('Failed to save cascade project to DB:', err))
    }
  },

  loadProject: (id) => {
    const project = get().savedProjects.find(p => p.id === id)
    if (!project || project.cascades.length === 0) return

    const first = project.cascades[0]
    const rest = project.cascades.slice(1)

    set({
      dealId: project.dealId,
      dealName: project.dealName,
      country: first.country,
      countryCode: first.countryCode,
      nodes: first.nodes.map(n => ({ ...n })),
      edges: first.edges.map(e => ({ ...e })),
      notes: first.notes,
      countryCascades: rest.map(c => ({ ...c, nodes: c.nodes.map(n => ({ ...n })), edges: c.edges.map(e => ({ ...e })) })),
      selectedNodeId: null,
      connectingFrom: null,
      activeChoice: null,
      fadingChoiceIds: [],
    })
  },

  deleteProject: (id) => {
    cascadeApi.deleteProject(id).catch((err) => console.error('Failed to delete cascade project from DB:', err))
    set((s) => ({
      savedProjects: s.savedProjects.filter(p => p.id !== id),
    }))
  },

  loadProjectsFromDb: async (dealId: number) => {
    try {
      const res = await cascadeApi.getProjects(dealId)
      if (res.success && res.data) {
        const dbProjects = res.data.map((p) => ({
          id: p.id,
          dealId: p.dealId,
          dealName: p.dealName,
          cascades: (p.cascades || []).map((c) => ({
            country: c.country,
            countryCode: c.countryCode,
            nodes: (c.nodes || []) as CascadeNode[],
            edges: (c.edges || []) as CascadeEdge[],
            notes: c.notes || '',
          })),
          updatedAt: p.updatedAt,
        }))
        // Merge: DB projects replace any matching localStorage projects for this deal
        set((s) => {
          const otherProjects = s.savedProjects.filter((p) => p.dealId !== dealId)
          return { savedProjects: [...otherProjects, ...dbProjects] }
        })
      }
    } catch (err) {
      console.error('Failed to load cascade projects from DB:', err)
    }
  },

  resetCascade: () => set({
    dealId: null,
    dealName: '',
    country: '',
    countryCode: '',
    countryCascades: [],
    nodes: [{ ...INITIAL_CUSTOMER_NODE }],
    edges: [],
    selectedNodeId: null,
    connectingFrom: null,
    activeChoice: null,
    fadingChoiceIds: [],
    notes: '',
  }),
}), {
  name: 'cascade-builder-storage',
  partialize: (state) => ({ savedProjects: state.savedProjects }),
}))
